import { convexQuery } from '@convex-dev/react-query'
import { api } from '@cvx/_generated/api'
import { Input } from '@rekrytering/ui/components/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@rekrytering/ui/components/select'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'

const STAGE_LABELS: Record<string, string> = {
  applied: 'Applied',
  screening: 'Screening',
  interview: 'Interview',
  offer: 'Offer',
  hired: 'Hired',
  rejected: 'Rejected',
}

const STAGE_COLORS: Record<string, string> = {
  applied: 'bg-gray-400',
  screening: 'bg-blue-400',
  interview: 'bg-purple-400',
  offer: 'bg-yellow-400',
  hired: 'bg-green-500',
  rejected: 'bg-red-400',
}

const FunnelChart = ({
  stages,
}: {
  stages: readonly {
    stage: string
    count: number
    dropOffPercent: number | null
  }[]
}) => {
  const maxCount = Math.max(...stages.map((s) => s.count), 1)

  return (
    <div className="flex flex-col gap-2">
      {stages.map((stageStat) => {
        const widthPercent = Math.round((stageStat.count / maxCount) * 100)
        const color = STAGE_COLORS[stageStat.stage] ?? 'bg-gray-400'
        const label = STAGE_LABELS[stageStat.stage] ?? stageStat.stage

        return (
          <div key={stageStat.stage} className="flex items-center gap-3">
            <span className="w-20 text-right text-xs text-primary/60">
              {label}
            </span>
            <div className="flex flex-1 items-center gap-2">
              <div className="flex-1 rounded-sm bg-secondary">
                <div
                  className={`${color} h-6 rounded-sm transition-all duration-300`}
                  style={{ width: `${widthPercent}%` }}
                />
              </div>
              <span className="w-8 text-right text-xs font-medium text-primary">
                {stageStat.count}
              </span>
              {stageStat.dropOffPercent !== null && (
                <span className="w-16 text-right text-xs text-primary/50">
                  ↓ {stageStat.dropOffPercent}%
                </span>
              )}
              {stageStat.dropOffPercent === null && <span className="w-16" />}
            </div>
          </div>
        )
      })}
    </div>
  )
}

export const PipelineReportPage = () => {
  const [fromDate, setFromDate] = useState('')
  const [toDate, setToDate] = useState('')
  const [selectedJobId, setSelectedJobId] = useState<string>('all')

  const fromAt = fromDate ? new Date(fromDate).getTime() : undefined
  const toAt = toDate ? new Date(toDate).getTime() : undefined

  const { data: report, isLoading } = useQuery(
    convexQuery(api.features.candidates.pipeline_report.fetchMyPipelineReport, {
      fromAt,
      toAt,
    }),
  )

  const jobOptions = report?.byJob ?? []
  const filteredJobs =
    selectedJobId === 'all'
      ? jobOptions
      : jobOptions.filter((j) => j.jobId === selectedJobId)

  return (
    <div className="flex h-full w-full flex-col px-6 py-8">
      <div className="mx-auto flex h-full w-full max-w-(--breakpoint-xl) flex-col gap-8">
        <div className="flex flex-wrap items-center gap-3">
          <Input
            type="date"
            className="w-40"
            value={fromDate}
            onChange={(e) => setFromDate(e.target.value)}
            placeholder="From"
          />
          <Input
            type="date"
            className="w-40"
            value={toDate}
            onChange={(e) => setToDate(e.target.value)}
            placeholder="To"
          />
          {jobOptions.length > 1 && (
            <Select
              value={selectedJobId}
              onValueChange={(v) => setSelectedJobId(v ?? 'all')}
            >
              <SelectTrigger className="w-56">
                <SelectValue placeholder="All jobs" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All active jobs</SelectItem>
                {jobOptions.map((j) => (
                  <SelectItem key={j.jobId} value={j.jobId}>
                    {j.jobTitle}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
        </div>

        {isLoading ? null : (
          <>
            {selectedJobId === 'all' && report && (
              <section className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <h2 className="text-base font-semibold text-primary">
                    Org-wide aggregate
                  </h2>
                  <p className="text-sm text-primary/60">
                    Across all active jobs
                  </p>
                </div>
                <div className="rounded-md border border-border bg-card p-5">
                  <FunnelChart stages={report.aggregate} />
                </div>
              </section>
            )}

            <section className="flex flex-col gap-6">
              {filteredJobs.length === 0 ? (
                <p className="text-sm text-primary/60">No active jobs found.</p>
              ) : (
                filteredJobs.map((jobReport) => (
                  <div key={jobReport.jobId} className="flex flex-col gap-3">
                    <h3 className="text-sm font-semibold text-primary">
                      {jobReport.jobTitle}
                    </h3>
                    <div className="rounded-md border border-border bg-card p-5">
                      <FunnelChart stages={jobReport.stages} />
                    </div>
                  </div>
                ))
              )}
            </section>
          </>
        )}
      </div>
    </div>
  )
}
